import { useAtomValue, useSetAtom } from 'jotai';

import {
	formatFinancingOfferStatus,
	formatPaymentStatus,
	labelFromPaymentChoice,
	OrderPaymentChoice,
	PurchasedVehicleFinancingOfferStatus,
	VehiclePaymentStatus,
} from '@ekhodealer/ekho-common';
import { Stack } from '@ekhodealer/ekho-common/rev';

import { AccordionSection } from '@common/components/AccordionSection';
import { FiltersDrawerCheckboxGroup,type FiltersDrawerCheckboxGroupItem } from '@common/components/FilterGroups/FiltersDrawerCheckboxGroup';

import {
	financingStatusFilterAtom,
	paymentChoiceFilterAtom,
	paymentStatusFilterAtom,
	updateFinancingStatusFilterAtom,
	updatePaymentChoiceFilterAtom,
	updatePaymentStatusFilterAtom,
} from '../../atoms/ordersAtoms';
import { useFilterFacetCounts } from '../../hooks/useFilterFacetCounts';

/**
 * FiltersDrawerOrderStatusSection component that provides order status filtering options
 * wrapped in an accordion
 */
export function FiltersDrawerOrderStatusSection() {
	const paymentChoices = useAtomValue(paymentChoiceFilterAtom);
	const paymentStatuses = useAtomValue(paymentStatusFilterAtom);
	const financingStatuses = useAtomValue(financingStatusFilterAtom);

	const setPaymentChoices = useSetAtom(updatePaymentChoiceFilterAtom);
	const setPaymentStatuses = useSetAtom(updatePaymentStatusFilterAtom);
	const setFinancingStatuses = useSetAtom(updateFinancingStatusFilterAtom);

	// Get dynamic facet counts from the search response
	const { paymentChoiceCounts, paymentStatusCounts, financingStatusCounts } =
		useFilterFacetCounts();

	// Build payment choice items with dynamic counts
	const paymentChoiceItems: FiltersDrawerCheckboxGroupItem[] = [
		{
			value: 'financing',
			label: labelFromPaymentChoice(OrderPaymentChoice.FINANCING) ?? 'Financing',
			count: paymentChoiceCounts['financing'] ?? 0,
		},
		{
			value: 'full-cash',
			label: labelFromPaymentChoice(OrderPaymentChoice.FULL_CASH) ?? 'Full cash',
			count: paymentChoiceCounts['full-cash'] ?? 0,
		},
	];

	// Build payment status items with dynamic counts
	const paymentStatusItems: FiltersDrawerCheckboxGroupItem[] = [
		{
			value: VehiclePaymentStatus.DOWNPAYMENT_PAID,
			label: formatPaymentStatus(VehiclePaymentStatus.DOWNPAYMENT_PAID),
			count: paymentStatusCounts[VehiclePaymentStatus.DOWNPAYMENT_PAID] ?? 0,
		},
		{
			value: VehiclePaymentStatus.RESERVATION_PAID,
			label: formatPaymentStatus(VehiclePaymentStatus.RESERVATION_PAID),
			count: paymentStatusCounts[VehiclePaymentStatus.RESERVATION_PAID] ?? 0,
		},
		{
			value: VehiclePaymentStatus.NO_PAYMENTS,
			label: formatPaymentStatus(VehiclePaymentStatus.NO_PAYMENTS),
			count: paymentStatusCounts[VehiclePaymentStatus.NO_PAYMENTS] ?? 0,
		},
		{
			value: VehiclePaymentStatus.FULLY_PAID,
			label: formatPaymentStatus(VehiclePaymentStatus.FULLY_PAID),
			count: paymentStatusCounts[VehiclePaymentStatus.FULLY_PAID] ?? 0,
		},
		{
			value: VehiclePaymentStatus.LOST_DISPUTE,
			label: formatPaymentStatus(VehiclePaymentStatus.LOST_DISPUTE),
			count: paymentStatusCounts[VehiclePaymentStatus.LOST_DISPUTE] ?? 0,
		},
	];

	// Build financing status items with dynamic counts
	const financingStatusItems: FiltersDrawerCheckboxGroupItem[] = [
		{
			value: PurchasedVehicleFinancingOfferStatus.AWAITING_LENDER_DECISIONS,
			label: formatFinancingOfferStatus(
				PurchasedVehicleFinancingOfferStatus.AWAITING_LENDER_DECISIONS,
			),
			count:
				financingStatusCounts[
					PurchasedVehicleFinancingOfferStatus.AWAITING_LENDER_DECISIONS
				] ?? 0,
		},
		{
			value: PurchasedVehicleFinancingOfferStatus.SOME_LENDERS_ACCEPTED,
			label: formatFinancingOfferStatus(
				PurchasedVehicleFinancingOfferStatus.SOME_LENDERS_ACCEPTED,
			),
			count:
				financingStatusCounts[PurchasedVehicleFinancingOfferStatus.SOME_LENDERS_ACCEPTED] ??
				0,
		},
		{
			value: PurchasedVehicleFinancingOfferStatus.ALL_LENDERS_REJECTED,
			label: formatFinancingOfferStatus(
				PurchasedVehicleFinancingOfferStatus.ALL_LENDERS_REJECTED,
			),
			count:
				financingStatusCounts[PurchasedVehicleFinancingOfferStatus.ALL_LENDERS_REJECTED] ??
				0,
		},
		{
			value: PurchasedVehicleFinancingOfferStatus.BUYER_ACCEPTED_SOME_OFFER,
			label: formatFinancingOfferStatus(
				PurchasedVehicleFinancingOfferStatus.BUYER_ACCEPTED_SOME_OFFER,
			),
			count:
				financingStatusCounts[
					PurchasedVehicleFinancingOfferStatus.BUYER_ACCEPTED_SOME_OFFER
				] ?? 0,
		},
		{
			value: PurchasedVehicleFinancingOfferStatus.BUYER_REJECTED_ALL_OFFERS,
			label: formatFinancingOfferStatus(
				PurchasedVehicleFinancingOfferStatus.BUYER_REJECTED_ALL_OFFERS,
			),
			count:
				financingStatusCounts[
					PurchasedVehicleFinancingOfferStatus.BUYER_REJECTED_ALL_OFFERS
				] ?? 0,
		},
	];

	return (
		<AccordionSection title='Payment'>
			<Stack itemsSpacing='24'>
				<FiltersDrawerCheckboxGroup
					label='Payment choice'
					items={paymentChoiceItems}
					value={paymentChoices}
					onChange={setPaymentChoices}
				/>
				<FiltersDrawerCheckboxGroup
					label='Payment status'
					items={paymentStatusItems}
					value={paymentStatuses}
					onChange={setPaymentStatuses}
				/>
				<FiltersDrawerCheckboxGroup
					label='Financing status'
					items={financingStatusItems}
					value={financingStatuses}
					onChange={setFinancingStatuses}
				/>
			</Stack>
		</AccordionSection>
	);
}
